import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { FUEL_TYPES, TRANSMISSION_TYPES } from "../shared/vehicleFleetConstants";

const emptyRow = () => ({ serviceName: "", spanTime: "", spanKm: "" });

// Mirrors maintenanceRegister()'s exact chain: serviceFor is required and
// restricted to "common" for bike models (bikes have no transmissionType/
// fuelType to key a template off), forValue is only meaningful (and only
// validated) when serviceFor is transmissionType/fuelType, and
// serviceDetails needs at least 1 row with no duplicate serviceName in the
// same submission. Posting against an existing modelId/serviceFor/forValue
// triple APPENDS these rows to that template rather than erroring, so this
// modal is always "add service(s)", never a full-template replace.
const MaintenanceFormModal = ({ modelId, brandType, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const isBike = brandType === "bikes";

    const [serviceFor, setServiceFor] = useState("common");
    const [forValue, setForValue] = useState("");
    const [rows, setRows] = useState([emptyRow()]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onServiceForChange = (value) => {
        setServiceFor(value);
        setForValue("");
    };

    const updateRow = (index, field, value) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    const addRow = () => setRows((prev) => [...prev, emptyRow()]);
    const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

    const onSubmit = async () => {
        if (serviceFor !== "common" && !forValue) {
            toast.error(serviceFor === "transmissionType" ? "Select a transmission type" : "Select a fuel type");
            return;
        }
        const cleaned = rows.filter((r) => r.serviceName.trim() || String(r.spanTime).trim() || String(r.spanKm).trim());
        if (!cleaned.length) {
            toast.error("Add at least 1 service");
            return;
        }
        const invalid = cleaned.some((r) => !r.serviceName.trim() || String(r.spanTime).trim() === "" || String(r.spanKm).trim() === "" || Number(r.spanTime) < 0 || Number(r.spanKm) < 0);
        if (invalid) {
            toast.error("Each service needs a name, a non-negative span (months), and a non-negative span (km)");
            return;
        }
        const names = cleaned.map((r) => r.serviceName.trim().toLowerCase());
        const duplicate = names.find((n, i) => names.indexOf(n) !== i);
        if (duplicate) {
            toast.error(`Duplicate service name in this submission: ${duplicate}`);
            return;
        }

        setSaving(true);
        const payload = {
            serviceFor,
            ...(serviceFor !== "common" ? { forValue } : {}),
            serviceDetails: cleaned.map((r) => ({ serviceName: r.serviceName.trim(), spanTime: Number(r.spanTime), spanKm: Number(r.spanKm) })),
        };
        const response = await Handler({ method: "post", url: `/admin/maintenance/model/${modelId}`, data: payload });
        if (response?.success) {
            toast.success(response?.data?.message || "Service(s) added");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the maintenance template"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">Add Maintenance Service</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3 mb-2">
                            <div className="col-12 col-md-6">
                                <label className="form-label">Applies to</label>
                                <select className="form-select" value={serviceFor} onChange={(e) => onServiceForChange(e.target.value)}>
                                    <option value="common">Common (every variant of this model)</option>
                                    <option value="transmissionType" disabled={isBike}>Transmission type{isBike ? " (cars only)" : ""}</option>
                                    <option value="fuelType" disabled={isBike}>Fuel type{isBike ? " (cars only)" : ""}</option>
                                </select>
                            </div>
                            {serviceFor === "transmissionType" ? (
                                <div className="col-12 col-md-6">
                                    <label className="form-label">Transmission type</label>
                                    <select className="form-select" value={forValue} onChange={(e) => setForValue(e.target.value)}>
                                        <option value="" disabled>Select transmission</option>
                                        {TRANSMISSION_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                                    </select>
                                </div>
                            ) : null}
                            {serviceFor === "fuelType" ? (
                                <div className="col-12 col-md-6">
                                    <label className="form-label">Fuel type</label>
                                    <select className="form-select" value={forValue} onChange={(e) => setForValue(e.target.value)}>
                                        <option value="" disabled>Select fuel type</option>
                                        {FUEL_TYPES.map((f) => (<option key={f} value={f}>{f}</option>))}
                                    </select>
                                </div>
                            ) : null}
                        </div>

                        <label className="form-label d-block">Services</label>
                        {rows.map((row, index) => (
                            <div key={index} className="row g-2 align-items-center mb-2">
                                <div className="col-12 col-md-5">
                                    <input type="text" className="form-control" placeholder="Service name (e.g. Oil change)" value={row.serviceName} onChange={(e) => updateRow(index, "serviceName", e.target.value)} />
                                </div>
                                <div className="col-6 col-md-3">
                                    <input type="number" className="form-control" placeholder="Every N months" min="0" value={row.spanTime} onChange={(e) => updateRow(index, "spanTime", e.target.value)} />
                                </div>
                                <div className="col-6 col-md-3">
                                    <input type="number" className="form-control" placeholder="Every N km" min="0" value={row.spanKm} onChange={(e) => updateRow(index, "spanKm", e.target.value)} />
                                </div>
                                <div className="col-12 col-md-1">
                                    {rows.length > 1 ? (
                                        <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeRow(index)}>&times;</button>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addRow}>+ Add another service</button>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MaintenanceFormModal;
